/**
 * CorrTab — rolling correlation matrix for watchlist symbols.
 * Computes from 1y daily closes pulled in parallel, then renders as a
 * Plotly heatmap. Cells are clickable to focus the symbol.
 */
import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { LazyPlot as Plot } from '@/components/LazyPlot';
import { useStore, readThemeColors } from '@/store';
import { api } from '@/lib/api';
import { FONT } from '@/theme';
import type { Bar } from '@/types';

export function CorrTab() {
  const symbols = useStore(s => s.watchlist);
  const theme   = useStore(s => s.theme);  // dep for theme-driven rebuild
  const { gain, loss } = readThemeColors();
  void theme;

  const queries = useQueries({
    queries: symbols.map(sym => ({
      queryKey: ['bars-corr', sym],
      queryFn:  () => api<Bar[]>(`/api/ohlcv/${encodeURIComponent(sym)}?period=1y&interval=1d`),
      staleTime: 600_000,
    })),
  });

  const allSettled = queries.every(q => !q.isLoading);
  const built = useMemo(() => {
    if (!allSettled) return null;
    // Per-symbol log-return series, keeping the label alongside so a symbol that
    // FAILED to load (or returned too few bars) is DROPPED from the matrix --
    // rather than collapsing the ENTIRE matrix to "NOT ENOUGH DATA", which is
    // what one bad fetch used to do: its empty series forced minLen=0 across
    // every pair. Non-finite closes are skipped too.
    const series = symbols.map((sym, i) => {
      const bars = queries[i]?.data ?? [];
      const r: number[] = [];
      for (let k = 1; k < bars.length; k++) {
        const a = bars[k]?.close, b = bars[k - 1]?.close;
        if (Number.isFinite(a) && Number.isFinite(b) && (b as number) > 0) {
          r.push(Math.log((a as number) / (b as number)));
        }
      }
      return { sym, r };
    }).filter(s => s.r.length >= 5);

    if (series.length < 2) return null;
    // Trim to the shortest SURVIVING series so correlations align.
    const minLen = Math.min(...series.map(s => s.r.length));
    const r = series.map(s => s.r.slice(-minLen));
    const labels = series.map(s => s.sym);
    const n = labels.length;
    const M: number[][] = [];
    for (let i = 0; i < n; i++) {
      M.push([]);
      for (let j = 0; j < n; j++) M[i].push(corr(r[i], r[j]));
    }
    const dropped = symbols.filter(s => !labels.includes(s));
    return { M, labels, dropped };
  }, [allSettled, queries, symbols]);

  if (!allSettled || !built) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100%', color: '#333', fontSize: 11, letterSpacing: 2, fontFamily: FONT,
      }}>{allSettled ? 'NOT ENOUGH DATA' : 'COMPUTING CORRELATIONS...'}</div>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {built.dropped.length > 0 && (
        <div style={{
          position: 'absolute', top: 4, left: 8, zIndex: 5,
          fontSize: 9, color: '#c08a3e', fontFamily: FONT,
          background: 'rgba(9,9,9,0.72)', padding: '1px 5px', borderRadius: 2,
        }}>
          no data: {built.dropped.join(', ')}
        </div>
      )}
      <Plot
        data={[{
          type: 'heatmap',
          z: built.M,
          x: built.labels, y: built.labels,
          colorscale: [
            [0,   loss],
            [0.5, '#1a1a1a'],
            [1,   gain],
          ],
          zmin: -1, zmax: 1,
          showscale: true,
          hovertemplate: '%{x} vs %{y}: %{z:.2f}<extra></extra>',
        } as unknown as Plotly.Data]}
        layout={{
          paper_bgcolor: '#090909', plot_bgcolor: '#090909',
          margin: { l: 60, r: 30, t: 8, b: 60 },
          font: { color: '#e0e0e0', family: 'Consolas, monospace', size: 10 },
          xaxis: { tickangle: -45, color: '#888', gridcolor: '#1a1a1a' },
          yaxis: { color: '#888', gridcolor: '#1a1a1a' },
        } as unknown as Partial<Plotly.Layout>}
        config={{ displayModeBar: false, responsive: true }}
        style={{ width: '100%', height: '100%' }}
        useResizeHandler
      />
    </div>
  );
}

function corr(a: number[], b: number[]): number {
  const n = Math.min(a.length, b.length);
  if (n < 2) return 0;
  let sa = 0, sb = 0;
  for (let i = 0; i < n; i++) { sa += a[i]; sb += b[i]; }
  const ma = sa / n, mb = sb / n;
  let cov = 0, va = 0, vb = 0;
  for (let i = 0; i < n; i++) {
    const da = a[i] - ma, db = b[i] - mb;
    cov += da * db; va += da * da; vb += db * db;
  }
  const denom = Math.sqrt(va * vb);
  return denom > 0 ? cov / denom : 0;
}
